import { combineEpics } from 'redux-observable'
import { Observable } from 'rxjs/Observable'

import { selectActualDefaultLanguage } from 'containers/App/selectors'
import { SCENARIO_TYPE_VALUES } from 'containers/AwarenessCenter/constants'

import {
  addScenarioError,
  addScenarioSuccess,
  createCampaignFromIncidentsError,
  createCampaignFromIncidentsSuccess,
  createCampaignFromScenarioSuccess,
  deleteScenarioError,
  deleteScenarioSuccess,
  getAwarenessDetailsError,
  getAwarenessDetailsSuccess,
  getScenarioError,
  getScenarioSuccess,
  reportToTemplateError,
  reportToTemplateSuccess,
  saveScenarioError,
  saveScenarioSuccess,
} from './actions'
import {
  ADD_SCENARIO_ASYNC,
  CREATE_CAMPAIGN_FROM_INCIDENTS_ASYNC,
  CREATE_CAMPAIGN_FROM_SCENARIO_ASYNC,
  DELETE_SCENARIO_ASYNC,
  GET_AWARENESS_DETAILS_ASYNC,
  GET_SCENARIO_ASYNC,
  parseFromEmail,
  REPORT_TO_TEMPLATE_ASYNC,
  SAVE_SCENARIO_ASYNC,
} from './constants'
import { DEFAULT_SCENARIO } from './reducer'

const filterContentData = (contents, scenarioLangValues) =>
  contents.filter(({ locale }) => scenarioLangValues.includes(locale))

const getScenarioBody = (scenario) => {
  const scenarioBody = {
    ...scenario,
    scenario_group_name: scenario.scenario_group,
  }

  scenarioBody.contents.forEach(({ from_email }, index) => {
    if (from_email[0] === ' ') {
      scenarioBody.contents[index].from_email = from_email.substring(1)
    }

    if (scenarioBody.contents[index].from_email.includes(' ')) {
      const { displayName, from, domain } = parseFromEmail(scenarioBody.contents[index].from_email)

      scenarioBody.contents[index].from_email = `${displayName} <${from}@${domain}>`
    }
  })

  if (scenarioBody.type !== SCENARIO_TYPE_VALUES.ATTACHMENT) {
    scenarioBody.filename = DEFAULT_SCENARIO.filename
  }

  return scenarioBody
}

const getScenarioEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(GET_SCENARIO_ASYNC.INITIATE).mergeMap((action) =>
    api.awarenessCenter
      .getScenario(
        store.getState().partnerOptions.selectedCompany?.value,
        !!store.getState().app.data.partner,
        action.payload.id
      )
      .map((response) =>
        getScenarioSuccess(response, selectActualDefaultLanguage(store.getState()))
      )
      .catch((error) => Observable.of(epicsErrorHandler(getScenarioError, error, [400])))
      .takeUntil(action$.ofType(GET_SCENARIO_ASYNC.CANCEL))
  )

const addScenarioEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(ADD_SCENARIO_ASYNC.INITIATE).mergeMap((action) =>
    api.awarenessCenter
      .addScenario(
        {
          ...getScenarioBody(store.getState().scenarioForm.scenario),
          contents: filterContentData(
            store.getState().scenarioForm.scenario.contents,
            store.getState().scenarioForm.scenarioLangValues
          ),
        },
        action.payload.companyId,
        !!store.getState().app.data.partner
      )
      .map(({ response }) => addScenarioSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(addScenarioError, error, [400])))
      .takeUntil(action$.ofType(ADD_SCENARIO_ASYNC.CANCEL))
  )

const saveScenarioEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(SAVE_SCENARIO_ASYNC.INITIATE).mergeMap((action) => {
    const scenarioBody = {
      ...getScenarioBody(store.getState().scenarioForm.scenario),
      contents: filterContentData(
        store.getState().scenarioForm.scenario.contents,
        store.getState().scenarioForm.scenarioLangValues
      ),
    }

    return api.awarenessCenter
      .saveScenario(
        action.payload.id,
        scenarioBody,
        store.getState().partnerOptions.selectedCompany?.value,
        !!store.getState().app.data.partner
      )
      .map(() => saveScenarioSuccess(scenarioBody))
      .catch((error) => Observable.of(epicsErrorHandler(saveScenarioError, error, [400])))
      .takeUntil(action$.ofType(SAVE_SCENARIO_ASYNC.CANCEL))
  })

const deleteScenarioEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(DELETE_SCENARIO_ASYNC.INITIATE).mergeMap(() =>
    api.awarenessCenter
      .deleteScenario(
        store.getState().scenarioForm.id,
        store.getState().partnerOptions.selectedCompany?.value,
        !!store.getState().app.data.partner
      )
      .map((response) => deleteScenarioSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(deleteScenarioError, error, [400])))
      .takeUntil(action$.ofType(DELETE_SCENARIO_ASYNC.CANCEL))
  )

const getAwarenessDetailsEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(GET_AWARENESS_DETAILS_ASYNC.INITIATE).mergeMap(() =>
    api.awarenessCenter
      .details(
        store.getState().partnerOptions.selectedCompany?.value,
        !!store.getState().app.data.partner
      )
      .map((response) => getAwarenessDetailsSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(getAwarenessDetailsError, error, [400])))
      .takeUntil(action$.ofType(GET_AWARENESS_DETAILS_ASYNC.CANCEL))
  )

const reportToTemplateEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(REPORT_TO_TEMPLATE_ASYNC.INITIATE).mergeMap((action) =>
    api.awarenessCenter
      .reportToTemplate(action.payload.reportId)
      .map((response) => reportToTemplateSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(reportToTemplateError, error, [400])))
      .takeUntil(action$.ofType(REPORT_TO_TEMPLATE_ASYNC.CANCEL))
  )

const createCampaignFromIncidentsEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(CREATE_CAMPAIGN_FROM_INCIDENTS_ASYNC.INITIATE).mergeMap((action) =>
    api.awarenessCenter
      .createNewCampaignFromScenario(
        action.payload.reportId,
        action.payload.localeId,
        action.payload.companyId,
        !!store.getState().app.data.partner
      )
      .map(({ response }) => createCampaignFromIncidentsSuccess(response))
      .catch((error) =>
        Observable.of(epicsErrorHandler(createCampaignFromIncidentsError, error, [400]))
      )
      .takeUntil(action$.ofType(CREATE_CAMPAIGN_FROM_INCIDENTS_ASYNC.CANCEL))
  )

const createCampaignFromScenarioEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(CREATE_CAMPAIGN_FROM_SCENARIO_ASYNC.INITIATE).mergeMap((action) =>
    api.awarenessCenter
      .createNewCampaignFromScenario(
        action.payload.id,
        action.payload.localeId,
        store.getState().partnerOptions.selectedCompany?.value,
        !!store.getState().app.data.partner
      )
      .map(({ response }) => createCampaignFromScenarioSuccess(response))
      .catch((error) =>
        Observable.of(epicsErrorHandler(createCampaignFromIncidentsError, error, [400]))
      )
      .takeUntil(action$.ofType(CREATE_CAMPAIGN_FROM_SCENARIO_ASYNC.CANCEL))
  )

export default combineEpics(
  getScenarioEpic,
  addScenarioEpic,
  deleteScenarioEpic,
  saveScenarioEpic,
  getAwarenessDetailsEpic,
  reportToTemplateEpic,
  createCampaignFromIncidentsEpic,
  createCampaignFromScenarioEpic
)
